import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  useTheme,
  IconButton,
  CircularProgress,
  Fade,
  Slide,
  useMediaQuery
} from '@mui/material';
import { TypographyVariant } from '@mui/material/styles';
import { ArrowBackIosNew, ArrowForwardIos, Circle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import blackLogo from '../assets/Black logo.png';

import { encodeId } from '../utils/idEncoder';

// ==============================
// TYPE DEFINITIONS
// ==============================

export interface ArticleData {
  articleId: number;
  articleCategory: string;
  articleTitle: string;
  articleDescription?: string;
  imageUrl: string;
  position: string;
  publishDate: string;
}

interface ArticleCardProps {
  article: ArticleData;
  aspectRatio?: string;
  titleVariant?: TypographyVariant;
  tagColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  isHero?: boolean;
}

interface CommercialArticlesSectionProps {
  articles: ArticleData[];
  isLoading?: boolean;
}

// ==============================
// REUSABLE ARTICLE CARD COMPONENT
// ==============================

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  aspectRatio = '16 / 9',
  titleVariant = 'h6',
  tagColor = 'primary',
  isHero = false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: isHero ? { xs: 0, sm: 4 } : 4, // Full width on mobile for hero
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isHero ? theme.shadows[8] : theme.shadows[3],
        aspectRatio: isMobile && isHero ? '1 / 1' : aspectRatio, // Square hero on mobile
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        '&:hover': {
          transform: isHero ? 'none' : 'translateY(-6px)',
          boxShadow: isHero ? theme.shadows[8] : theme.shadows[10],
        }
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/articles/${encodeId(article.articleId)}`)}
        sx={{ height: '100%', width: '100%' }}
      >
        <CardMedia
          component="img"
          image={article.imageUrl}
          alt={article.articleTitle}
          sx={{
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            transition: 'transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
            '&:hover': { transform: 'scale(1.05)' },
          }}
        />
        
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            // Deeper gradient for better text legibility
            background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
            padding: isHero ? { xs: 2, md: 4 } : 2.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            height: '100%', 
            pointerEvents: 'none' // Let clicks pass through to CardActionArea
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, transform: 'translateZ(0)' }}>
            <Chip
              label={article.articleCategory}
              color={tagColor}
              size="small"
              sx={{ 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.7rem',
                mb: 0.5,
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            />
            
            <Typography
              variant={titleVariant}
              component="h3"
              sx={{
                color: 'white',
                fontWeight: 800,
                lineHeight: 1.1,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: isHero ? 3 : 2,
              }}
            >
              {article.articleTitle}
            </Typography>

            {isHero && article.articleDescription && (
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  mt: 1,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  // ✅ FIX: Consolidated display property here
                  display: { xs: 'none', sm: '-webkit-box' } 
                }}
              >
                {article.articleDescription}
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
              {formatDate(article.publishDate)}
              {isHero && <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.5)' }} />}
              {isHero && '5 min read'}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

// ==============================
// MAIN SECTION COMPONENT
// ==============================

const CommercialArticlesSection: React.FC<CommercialArticlesSectionProps> = ({ 
  articles, 
  isLoading = false 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Separate Hero & Grid Data
  const { heroArticles, gridArticles } = useMemo(() => {
    if (!articles || articles.length === 0) return { heroArticles: [], gridArticles: [] };

    let heroes = articles.filter(a => a.position === 'hero');
    let grid = articles.filter(a => a.position === 'grid');

    // Fallback if no hero designated
    if (heroes.length === 0 && articles.length > 0) {
      heroes.push(articles[0]);
      grid = grid.filter(a => a.articleId !== articles[0].articleId);
    } else {
      // Ensure hero isn't duplicated in grid
      const heroIds = heroes.map(h => h.articleId);
      grid = grid.filter(a => !heroIds.includes(a.articleId));
    }

    return { heroArticles: heroes, gridArticles: grid };
  }, [articles]);

  // Auto-slide effect
  useEffect(() => {
    if (heroArticles.length <= 1) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % heroArticles.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroArticles.length]);

  const handleNext = () => setActiveStep((prev) => (prev + 1) % heroArticles.length);
  const handleBack = () => setActiveStep((prev) => (prev - 1 + heroArticles.length) % heroArticles.length);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress color="inherit" /></Box>;
  }

  if (heroArticles.length === 0 && gridArticles.length === 0) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', pb: 8, px: { xs: 0, sm: 2 } }}>
      


      <Grid container spacing={3}>
        
        {/* --- HERO SLIDER SECTION --- */}
        {heroArticles.length > 0 && (
          <Grid size={{xs:12}} sx={{ mb: 2 }}>
            <Box 
              sx={{ 
                position: 'relative', 
                borderRadius: { xs: 0, sm: 4 }, 
                overflow: 'hidden',
                '&:hover .slider-nav': { opacity: 1, transform: 'translateY(-50%) scale(1)' } 
              }}
            >
              {/* Animated Slide Transition */}
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                 <ArticleCard
                    key={activeStep} // Key forces re-render for animation
                    article={heroArticles[activeStep]}
                    aspectRatio="21 / 9" // Cinematic ratio for desktop
                    titleVariant={isMobile ? "h5" : "h3"}
                    tagColor="warning"
                    isHero={true}
                  />
              </Box>

              {/* Slider Controls */}
              {heroArticles.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <IconButton
                    className="slider-nav"
                    onClick={handleBack}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: { xs: 8, md: 24 },
                      transform: { xs: 'translateY(-50%)', md: 'translateY(-50%) scale(0.9)' },
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      opacity: { xs: 1, md: 0 }, // Always visible on mobile
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'translateY(-50%) scale(1.1)' },
                      zIndex: 2,
                      width: { xs: 36, md: 48 },
                      height: { xs: 36, md: 48 },
                    }}
                  >
                    <ArrowBackIosNew fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>

                  {/* Right Arrow */}
                  <IconButton
                    className="slider-nav"
                    onClick={handleNext}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: { xs: 8, md: 24 },
                      transform: { xs: 'translateY(-50%)', md: 'translateY(-50%) scale(0.9)' },
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      opacity: { xs: 1, md: 0 },
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'translateY(-50%) scale(1.1)' },
                      zIndex: 2,
                      width: { xs: 36, md: 48 },
                      height: { xs: 36, md: 48 },
                    }}
                  >
                    <ArrowForwardIos fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>

                  {/* Dots Indicator */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: { xs: 16, md: 24 }, 
                      right: { xs: 16, md: 32 }, 
                      display: 'flex', 
                      gap: 1, 
                      zIndex: 2,
                      bgcolor: 'rgba(0,0,0,0.3)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 20,
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    {heroArticles.map((_, index) => (
                      <Circle
                        key={index}
                        sx={{
                          fontSize: 8,
                          color: index === activeStep ? 'white' : 'rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          transform: index === activeStep ? 'scale(1.2)' : 'scale(1)'
                        }}
                        onClick={() => setActiveStep(index)}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        )}

        {/* --- GRID ARTICLES SECTION --- */}
        {gridArticles.map((article, index) => (
          <Grid size={{xs:12, sm: 6, md: 4}}  key={article.articleId}>
            {/* Staggered Entrance Animation */}
            <Fade in={true} timeout={500 + index * 100}> 
              <Box sx={{ height: '100%', px: { xs: 2, sm: 0 } }}> {/* Added padding for mobile grid items */}
                <ArticleCard
                  article={article}
                  aspectRatio="3 / 4" // Tall portrait ratio for grid
                  titleVariant="h6"
                  tagColor={index % 2 === 0 ? 'primary' : 'secondary'}
                />
              </Box>
            </Fade>
          </Grid>
        ))}

      </Grid>
    </Box>
  );
};

export default CommercialArticlesSection;